import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';

import { WhiteBox, CustomScrollbar, FamilyPoints } from 'components/flink-play';
import { StarIcon } from 'components/flink-play/FlinkPlayIcons/FlinkPlayIcons';
import { imagesURL } from 'config';
import classes from './MainMenuBoard.module.scss';
import SupportPhone from 'components/common/SupportPhone';
import { isMobileAppRuntime } from 'utils';

const GENERATED_CUSTOM_LEVEL_AGE_LABELS = {
  age_4_8: { stringNumber: 3809, fallback: 'Ages 4–8' },
  age_9_13: { stringNumber: 3810, fallback: 'Ages 9–13' },
  age_14_18: { stringNumber: 3811, fallback: 'Ages 14–18' },
  adult: { stringNumber: 3812, fallback: 'Adults' },
};

// The generated menu heading is derived from the authored String 3844, not
// from the English "Custom Level" fallback or the separate Login locale.
// The notification template contains a variable level number and a trailing
// creation message. Keep only the localized label before that variable.
const getGeneratedCustomLevelTitle = ({ strings, solutionLocale, translate, level }) => {
  const code = String(solutionLocale?.code || '').trim().toLowerCase().split(/[-_]/)[0];
  const record = (strings || []).find((item) => Number(item?.stringNumber) === 3844);
  const authored = record?.data?.[code];
  const authoredText = authored?.stringValue || authored?.prettyValue || '';
  const translatedText = typeof translate === 'function'
    ? translate(3844, 'Custom Level {{level}} created.', true)
    : '';
  const localizedTemplate = String(authoredText || translatedText || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();

  // Translators may localize the placeholder text as well as the rest of the
  // template: "(variable number)", "(número variable)", "(số biến)", etc.
  // Therefore the marker must not depend on English placeholder vocabulary.
  const marker = /\{\{[^{}]+\}\}|\$\{[^{}]+\}|\{[^{}]+\}|\[[^\x5B\x5D]+\]|\([^()]+\)|%\d*\$?[ds]|\b\d+\b/.exec(localizedTemplate);
  if (marker) {
    const localizedLabel = localizedTemplate.slice(0, marker.index).trim();
    if (localizedLabel) return `${localizedLabel} ${level}`;
  }

  // Do not silently replace an existing localization with English when its
  // variable notation differs from the English template. Preserve the text.
  if (localizedTemplate && localizedTemplate !== 'Translate not found') {
    return `${localizedTemplate} ${level}`;
  }
  return `Custom Level ${level}`;
};

const MainMenuBoard = (props) => {
  const mobileRuntime = isMobileAppRuntime();
  const {
    title,
    subtitle,
    solutionMenu,
    translate,
    strings = [],
    solutionLocale,
    learners = [],
    learnerGoal,
    openMenuItem,
    scrollPosition,
    setScrollPosition,
    scrollbarRef,
    children,
    noReports = false,
    points = false,
    fromIndividualReports,
    purchasedProductIds = [], 
  } = props;

  const { menuGroups } = solutionMenu;
  const isMobileGeneratedCustomLevel =
    mobileRuntime && solutionMenu.generatedCustomLevelIndex != null;
  const generatedContextAgeBand = String(solutionMenu.generatedCustomContentContextKey || '').split(':')[0];
  const generatedAgeBand = solutionMenu.generatedCustomContentAgeBand || generatedContextAgeBand || '';
  const generatedAgeLabel = GENERATED_CUSTOM_LEVEL_AGE_LABELS[generatedAgeBand];
  // Resolve once at the actual render boundary. The parent must not supply a
  // competing English title for generated Custom Levels.
  const effectiveTitle = props.generatedCustomLevelTitle
    ? getGeneratedCustomLevelTitle({
        strings,
        solutionLocale,
        translate,
        level: solutionMenu.generatedCustomLevelIndex || 1,
      })
    : title;
  const effectiveSubtitle = isMobileGeneratedCustomLevel && generatedAgeLabel
    ? translate(generatedAgeLabel.stringNumber, generatedAgeLabel.fallback)
    : subtitle;

  const isSolutionPurchased = useMemo(() => {
    if (!solutionMenu.productId) return true;
    return purchasedProductIds.includes(solutionMenu.productId);
  }, [solutionMenu.productId, purchasedProductIds]);

  const levels = useMemo(() => {
    if (!isSolutionPurchased) return [];
    return _.chain(menuGroups)
      .map((g) => (isNaN(+g.menuLevel) ? g.menuLevel : +g.menuLevel))
      .uniq()
      .sort((a, b) => {
        if (isNaN(a) && isNaN(b)) return a > b ? 1 : -1;
        if (isNaN(a)) return 1;
        if (isNaN(b)) return -1;
        return b - a;
      })
      .value();
  }, [menuGroups, isSolutionPurchased]);

  const firstCategories = useMemo(() => {
    if (!isSolutionPurchased) return [];
    return _.chain(menuGroups)
      .map((g) => g.firstCategory)
      .uniq()
      .map((id) => {
        const generatedMenuGroup = menuGroups.find((group) => group.firstCategory === id) || {};
        return {
          id,
          generatedSubject: generatedMenuGroup.generatedSubject,
          generatedSubjectLabel: generatedMenuGroup.generatedSubjectLabel,
          generatedSubjectImage: generatedMenuGroup.generatedSubjectImage,
          ...(solutionMenu.reports[id] || {}),
        };
      })
      .sortBy('order')
      .value();
  }, [menuGroups, solutionMenu.reports, isSolutionPurchased]);

  if (!isSolutionPurchased) {
    return (
      <WhiteBox outerClass={classes.wrapper} innerClass={classnames(classes.innerWrapper, { [classes.mobileInnerWrapper]: mobileRuntime })}>
        <div className={classes.header}>
          <div className={classes.logoWrapper}>
            <img
              src="https://s3.us-east-2.amazonaws.com/flink-2.0-fs/Files/LandingPageAssets/Logo/Family_Logo_2024.svg"
              alt="Familylearningcompany"
            />
          </div>
          <div className={classes.titleAndSubtitleWrapper}>
            <h1 className={classes.menuTitle}>{effectiveTitle}</h1>
            <h2 className={classes.menuSubtitle}>{effectiveSubtitle}</h2>
          </div>
        </div>

        <div className={classes.notPurchased}>
          <p>
            {translate(999, 'This module is not purchased. Please select another in Settings.')}
          </p>
          <SupportPhone />
        </div>

        {children}
      </WhiteBox>
    );
  }

  const {
    buttonImage,
    levelTitle: levelTitleStringNumber,
    levelNames,
  } = solutionMenu;

  let hasTitles = false;

  const levelsWithNames = levels.map((level) => {
    const translatedName =
      levelNames &&
      levelNames[level] &&
      translate(levelNames[level], level, true);

    translatedName && (hasTitles = true);
    return { level, name: translatedName };
  });

  const uniqueLoggedInLearnerIds = new Set(
    learners
      .map((learner) => learner?._id || learner?.id || learner?.username)
      .filter(Boolean)
      .map(String)
  );
  const shouldShowFamilyPoints =
    Boolean(points) && uniqueLoggedInLearnerIds.size >= 2;

  return (
    <WhiteBox outerClass={classes.wrapper} innerClass={classnames(classes.innerWrapper, { [classes.mobileInnerWrapper]: mobileRuntime })}>
      <div className={classes.header}>
        <div className={classes.logoWrapper}>
          <img
            src="https://s3.us-east-2.amazonaws.com/flink-2.0-fs/Files/LandingPageAssets/Logo/Family_Logo_2024.svg"
            alt="Familylearningcompany"
          />
        </div>

        {shouldShowFamilyPoints && <FamilyPoints />}

        <div className={classes.titleAndSubtitleWrapper}>
          <h1 className={classes.menuTitle}>{effectiveTitle}</h1>
          {/* Age is shown in the grades header below. Do not print it twice. */}
        </div>
      </div>

      <div
        className={classnames(classes.table, {
          [classes.mobileCustomLevelTable]: isMobileGeneratedCustomLevel,
        })}
      >
        <div className={classes.tableHeading}>
          <div>
            {!noReports && (
              <ul className={classes.legend}>
                <li>
                  <span style={{ color: 'yellow' }} className={classes.legendStar}>
                    <StarIcon partial />
                  </span>
                  <span>{translate(220, 'Partial')}</span>
                </li>
                <li>
                  <span style={{ color: 'green' }} className={classes.legendStar}>
                    <StarIcon mastered />
                  </span>
                  <span>{translate(221, 'Mastered')}</span>
                </li>
              </ul>
            )}

            <span
              className={classnames(classes.levelsIndicator, {
                [classes.hidden]: levels.length < 8,
              })}
            >
              {translate(levelTitleStringNumber || 107, 'Level')}{' '}
              {levels[levels.length - 1]} - {levels[0]}
            </span>
          </div>
          <div className={classes.gradesHeading}>{effectiveSubtitle}</div>    
          {hasTitles && <figure></figure>}

          {firstCategories.map((cat) => {
            const displayName = cat.name
              ? translate(cat.name)
              : (cat.generatedSubjectLabel
                || (cat.id === "Amp9usX7" ? translate("986") : cat.id || "—"));
            const imageName = cat.image || cat.generatedSubjectImage;

            return (
              <figure key={cat.id}>
                <img
                  src={imageName && `${imagesURL}/ReportMenuImages/${imageName}`}
                  alt={displayName}
                />
                <figcaption>{displayName}</figcaption>
              </figure>
            );
          })}
        </div>

        <CustomScrollbar
          ref={scrollbarRef}
          style={{ height: '47vmin' }}
          initialValue={scrollPosition}
          onChange={(scroll) => setScrollPosition && setScrollPosition(scroll)}
        >
          {levelsWithNames.map(({ level, name }) => {
            return (
              <div key={level} className={classes.levelRow}>
                <span className={classes.levelTitle}>{level}</span>

                {hasTitles && (
                  <div className={classes.cell}>
                    <div className={classes.levelName}>{name}</div>
                  </div>
                )}

                {firstCategories.map((cat) => {
                  const menuGroup = _.find(menuGroups, {
                    firstCategory: cat.id,
                    menuLevel: level + '',
                  });

                  return (
                    <div key={cat.id} className={classes.cell}>
                      {menuGroup && (
                        <>
                          <button
                            onClick={() =>
                              openMenuItem &&
                              openMenuItem(menuGroup.groupId, true)
                            }
                            disabled={!openMenuItem}
                            className={classnames(classes.menuGroupBtn, {
                              [classes.highlight]:
                                learnerGoal &&
                                learnerGoal.level === level + '' &&
                                learnerGoal.subject === cat.id,
                            })}
                          >
                            {noReports && buttonImage ? (
                              <img src={buttonImage} alt="" />
                            ) : (
                              learners.map((l, idx) => {
                                const feedback =
                                  l.reports?.[menuGroup.groupId] || {};
                                const tooltip =
                                  feedback.dateMastered &&
                                  new Date(feedback.dateMastered);

                                return (
                                  <div
                                    key={idx}
                                    title={
                                      (tooltip && 'Mastered at ' + tooltip) || ''
                                    }
                                  >
                                    <StarIcon
                                      idx={idx}
                                      mastered={feedback.mastered}
                                      partial={feedback.partial}
                                    />
                                  </div>
                                );
                              })
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </CustomScrollbar>

        <div className={classes.footer}>
          <SupportPhone />

          {fromIndividualReports && (
            <div className={classes.timeSpentPreviousWeek}>
              <p>{translate(1012, 'Time spent during previous week:')} {props.previousWeekTime}</p>
            </div>
          )}

          {(learners.length > 1 && !fromIndividualReports) && (
            <div className={classes.learnerStarLegend}>
              {learners.map((learner, idx) => (
                <div
                  key={learner._id}
                  className={classes.learnerStarLegendItem}
                >
                  <span>{learner.username}</span>
                  <StarIcon idx={idx} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {children}
    </WhiteBox>
  );
};

MainMenuBoard.propTypes = {
  solutionMenu: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  strings: PropTypes.array,
  solutionLocale: PropTypes.object,
  generatedCustomLevelTitle: PropTypes.bool,
  learners: PropTypes.array,
  scrollPosition: PropTypes.object,
  setScrollPosition: PropTypes.func,
  openMenuItem: PropTypes.func,
  learnerGoal: PropTypes.object,
  fromIndividualReports: PropTypes.bool,
  familyTeam: PropTypes.bool,
  purchasedProductIds: PropTypes.array,
};

export default MainMenuBoard;